import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import React, { FC, useMemo } from 'react';
import { QueryFactory } from '@lmt/shared-types';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { AddDocumentButton } from './AddDocumentButton';
import { DocumentItem } from './DocumentItem';
import { groupBy } from 'lodash';
import { getFormattedLongDateTime } from '@lmt/unicorn-shared/app/components/patients/common';
import { DocumentGroupHeader } from './DocumentGroupHeader';
import { FileType } from '@lmt/shared-types';

import styles from './DocumentsList.module.scss';
import { defaultExcludedFileTypes } from './common';

interface Props {
  patientId: number;
  dataBlockId: number;
  visitId: number | undefined;
  isReadOnly?: boolean;
}

export const DocumentsList: FC<Props> = React.memo(
  function DocumentsList(props) {
    const { dataBlockId, patientId, isReadOnly } = props;
    const claims = useClaims();
    const i18next = useScopedTranslation('patients.documents');

    const documents = QueryFactory.FileQuery.useSearchFilesQuery({
      dataBlockId: dataBlockId,
      excludeFileType: defaultExcludedFileTypes,
      includeTransportStatuses: true,
    });

    const groupedDocuments = useMemo(() => {
      return groupBy(
        (documents.data ?? []).sort(
          (a, b) => b.uploadDateTime!.getTime() - a.uploadDateTime!.getTime(),
        ),
        (document) =>
          getFormattedLongDateTime(document.uploadDateTime) +
          '&' +
          (document.type === FileType.External ? document.comment : ''),
      );
    }, [documents.data]);

    return (
      <div className={styles.container} data-test-id="document-list">
        <div className={styles.header}>
          <div className={styles.title}>{i18next.t('title')}</div>
          {!isReadOnly && claims.permissions.createOrEditDocuments && (
            <AddDocumentButton
              patientId={patientId}
              dataBlockId={dataBlockId}
              visitId={props.visitId}
            />
          )}
        </div>
        <div>
          {Object.keys(groupedDocuments).map((createDate) => (
            <div
              key={createDate}
              className={styles.documentGroupContainer}
              data-test-id="document-group"
            >
              <DocumentGroupHeader
                dateString={createDate.split('&')[0]}
                comment={
                  groupedDocuments[createDate][0].type === FileType.External
                    ? groupedDocuments[createDate][0].comment
                    : undefined
                }
              />
              <div
                data-test-id="document-group-items"
                className={styles.documentGroupItems}
              >
                {groupedDocuments[createDate].map((document) => (
                  <div key={document.id} className={styles.documentItem}>
                    <DocumentItem
                      key={document.id}
                      document={document}
                      isReadOnly={isReadOnly}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  },
);
